/**
 * @enum FormFieldUIType
 * @description
 * **Form field UI type constants** for determining the visual presentation and behavior
 * of form field components in the SADIN Angular application.
 * 
 * This enum defines the different UI types that form fields can have, affecting
 * their styling, behavior, and CSS class generation. Each type corresponds to
 * a specific Bootstrap form component pattern.
 * 
 * **Usage Context:**
 * - Form field styling and class generation
 * - Bootstrap form component integration
 * - Dynamic UI behavior determination
 * - CSS class prefix selection
 * 
 * @example
 * // In component
 * public uiType: FormFieldUI = FormFieldUI.Control;
 * 
 * @example
 * // In utility function
 * const cssClass = FormFieldUtils.classStyleSize(size, FormFieldUI.Control);
 * 
 * @example
 * // In template
 * <div [class]="getFieldClasses(FormFieldUI.Select)">
 */
export enum FormFieldUI {

    /**
     * @description Standard form control (input, textarea) with form-control classes
     * @value 'control'
     * @example
     * // Generates: form-control-sm, form-control-lg
     * const cssClass = FormFieldUtils.classStyleSize(size, FormFieldUI.Control);
     */
    Control = 'control',

    /**
     * @description Select dropdown with form-select classes
     * @value 'select'
     * @example
     * // Generates: form-select-sm, form-select-lg
     * const cssClass = FormFieldUtils.classStyleSize(size, FormFieldUI.Select);
     */
    Select = 'select',
    
    /**
     * @description Input group with input-group classes
     * @value 'group'
     * @example
     * // Generates: input-group-sm, input-group-lg
     * const cssClass = FormFieldUtils.classStyleSize(size, FormFieldUI.Group);
     */
    Group = 'group'
}

/**
 * @description
 * **Form field UI type** for controlling the visual presentation and behavior
 * of form field components in the SADIN Angular application.
 * 
 * This type provides type-safe constants for form field UI types, ensuring
 * consistent UI types across all form components.
 */
export type FormFieldUIType = 'control' | 'select' | 'group';

/**
 * @description
 * **Form field UI type** for controlling the visual presentation and behavior
 * of form field components in the SADIN Angular application.
 * 
 * This type provides type-safe constants for form field UI types, ensuring
 * consistent UI types across all form components.
 */
export type IFormFieldUIType = FormFieldUIType | FormFieldUI;
